import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useVirtualizer } from '@tanstack/react-virtual'
import { VfxShotDetailContent } from '@/components/vfx-shots/VfxShotDetailContent'
import { ColumnPicker, getMetadataDisplayName } from '@/components/clips/ColumnPicker'
import { EntityDetailSheet } from '@/components/shared/EntityDetailSheet'
import { LinkRelationshipDialog } from '@/components/relationships/LinkRelationshipDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useVfxShots, useUpdateVfxShot, useDeleteVfxShot, useCreateVfxShot, useBulkUpdateVfxShotTags } from '@/hooks/useVfxShots'
import { useClips } from '@/hooks/useClips'
import { useClipSelection } from '@/hooks/useClipSelection'
import { useEntitySearch } from '@/hooks/useEntitySearch'
import { useEntityEdit } from '@/hooks/useEntityEdit'
import { useTags } from '@/hooks/useTags'
import { linkClipToVfxShot } from '@/lib/api/vfxShotClips'
import { Wand2, Search, Clock, CheckCircle, AlertCircle, XCircle, ArrowRight } from 'lucide-react'
import { TagBadge } from '@/components/tags/TagBadge'
import { BulkActionBar } from '@/components/shared/BulkActionBar'
import { TagsDialog } from '@/components/shared/TagsDialog'
import { EntityRowActions } from '@/components/shared/EntityRowActions'
import { ExpandableRowButton } from '@/components/shared/ExpandableRowButton'
import { TagsDisplayCell } from '@/components/shared/TagsDisplayCell'
import { SelectableTableRow } from '@/components/shared/SelectableTableRow'
import { ExpandedMetadataRow } from '@/components/shared/ExpandedMetadataRow'
import { toast } from 'sonner'
import type { Project, VfxShot, VfxShotStatus } from '@/types/database'
import { usePermissions } from '@/hooks/usePermissions'
import { useAnalytics } from '@/hooks/useAnalytics'

interface VfxViewConfig {
  visibleMetadataColumns: string[]
}

interface ProjectVfxTabProps {
  project: Project
}

export function ProjectVfxTab({ project }: ProjectVfxTabProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: vfxData, isLoading: vfxLoading, error } = useVfxShots(project.id, { limit: 100 })
  const { data: clips = [] } = useClips(project.id)
  const { data: tags = [] } = useTags(project.id)
  const deleteVfxShot = useDeleteVfxShot()
  const updateVfxShot = useUpdateVfxShot()
  const createVfxShot = useCreateVfxShot()
  const permissions = usePermissions(project.id, project.owner_user_id)
  const { trackVfxShotCreated } = useAnalytics()

  const vfxShots = vfxData?.shots || []

  // Bulk operations mutations
  const bulkUpdateTags = useBulkUpdateVfxShotTags()

  // UI State
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [expandedShotId, setExpandedShotId] = useState<string | null>(null)
  const [selectedMetadataColumns, setSelectedMetadataColumns] = useState<string[]>([])
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [selectedShot, setSelectedShot] = useState<VfxShot | null>(null)
  const [detailSheetOpen, setDetailSheetOpen] = useState(false)
  const [detailShotId, setDetailShotId] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<VfxShotStatus | 'all'>('all')
  const [tagsDialogMode, setTagsDialogMode] = useState<'single' | 'bulk' | null>(null)
  const [sortBy, setSortBy] = useState<'default' | 'tag' | 'name'>('default')

  // Check for shot query parameter and open detail if present
  useEffect(() => {
    const shotParam = searchParams.get('shot')
    if (shotParam && vfxShots) {
      const shot = vfxShots.find(s => s.id === shotParam)
      if (shot) {
        setDetailShotId(shotParam)
        setDetailSheetOpen(true)
        // Remove the query parameter from URL
        searchParams.delete('shot')
        setSearchParams(searchParams, { replace: true })
      }
    }
  }, [searchParams, vfxShots, setSearchParams])

  // Shared hooks for state management
  const editState = useEntityEdit<VfxShot>()

  // Search and filter with shared hook - now supports column-specific syntax
  // Field map for column-specific search (e.g., "shot:sc001", "status:pending")
  const fieldMap = useMemo(() => {
    const map: Record<string, number> = {
      'shot': 0,
      'shot name': 0,
      'name': 0,
      'status': 1,
      'notes': 2,
      'description': 2,
    }

    // Add custom metadata fields to the map dynamically
    if (vfxShots && vfxShots.length > 0) {
      const customFields = new Set<string>()
      vfxShots.forEach(shot => {
        if (shot.custom_metadata) {
          Object.keys(shot.custom_metadata).forEach(_key => customFields.add(_key))
        }
      })

      // Map custom fields (starting after core fields + tags)
      Array.from(customFields).forEach((field, index) => {
        const normalizedField = field.toLowerCase()
        map[normalizedField] = 3 + (vfxShots[0]?.tags?.length || 0) + index
      })
    }

    return map
  }, [vfxShots])

  const { searchQuery, setSearchQuery, filteredItems: searchFilteredShots } = useEntitySearch(
    vfxShots || [],
    (shot) => [
      shot.shot_name,
      shot.status,
      shot.notes,
      ...(shot.tags || []),
      ...Object.entries(shot.custom_metadata || {}).map(([_key, value]) =>
        typeof value === 'string' ? value : JSON.stringify(value)
      )
    ],
    fieldMap
  )

  // Apply status filter on top of search filter
  const filteredShots = useMemo(() => {
    if (filterStatus === 'all') return searchFilteredShots
    return searchFilteredShots.filter(shot => shot.status === filterStatus)
  }, [searchFilteredShots, filterStatus])

  // Sort and group VFX shots based on sortBy selection
  const sortedAndGroupedShots = useMemo(() => {
    if (sortBy === 'default') {
      return [{ tag: null, items: filteredShots }]
    }

    const sorted = [...filteredShots].sort((a, b) => {
      if (sortBy === 'tag') {
        const aTag = a.tags?.[0] || 'Untagged'
        const bTag = b.tags?.[0] || 'Untagged'
        return aTag.localeCompare(bTag)
      } else if (sortBy === 'name') {
        return (a.shot_name || '').localeCompare(b.shot_name || '')
      }
      return 0
    })

    if (sortBy === 'tag') {
      // Group by tag combination (all tags together)
      const grouped = new Map<string, VfxShot[]>()
      sorted.forEach(shot => {
        const tags = shot.tags || []
        // Create a key from all tags joined together
        const tagKey = tags.length > 0 ? tags.join(', ') : 'Untagged'
        if (!grouped.has(tagKey)) {
          grouped.set(tagKey, [])
        }
        grouped.get(tagKey)!.push(shot)
      })

      // Convert to array and sort tag names alphabetically
      return Array.from(grouped.entries())
        .sort(([a], [b]) => {
          // Put "Untagged" last
          if (a === 'Untagged') return 1
          if (b === 'Untagged') return -1
          return a.localeCompare(b)
        })
        .map(([tag, items]) => ({ tag, items }))
    }

    return [{ tag: null, items: sorted }]
  }, [filteredShots, sortBy])

  // Flatten shots for selection (needed for multi-select)
  const flattenedShots = useMemo(() => {
    return sortedAndGroupedShots.flatMap(group => group.items)
  }, [sortedAndGroupedShots])

  // Virtual scrolling setup
  const parentRef = useRef<HTMLDivElement>(null)

  const rowVirtualizer = useVirtualizer({
    count: flattenedShots.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 50, // Estimated row height in pixels
    overscan: 10, // Number of items to render outside visible area
  })

  // Multi-select state
  const selection = useClipSelection(vfxShots || [], flattenedShots)

  // Create dialog state
  const [formData, setFormData] = useState({
    shot_name: '',
    description: '',
    clip_id: '',
    tags: [] as string[],
  })
  const [clipSearchQuery, setClipSearchQuery] = useState('')

  // Extract all unique field names from VFX shots
  const availableMetadataFields = useMemo(() => {
    const fieldsSet = new Set<string>()

    // Add core VFX shot fields
    fieldsSet.add('Shot Name')
    fieldsSet.add('Description')
    fieldsSet.add('Status')
    fieldsSet.add('Frame Range')
    fieldsSet.add('Duration')
    fieldsSet.add('Priority')
    fieldsSet.add('Due Date')
    fieldsSet.add('Assigned User')

    return Array.from(fieldsSet)
  }, [])

  // Load view config from localStorage on mount
  useEffect(() => {
    if (availableMetadataFields.length === 0) return

    const savedConfig = localStorage.getItem(`vfxView_v2_${project.id}`)

    if (savedConfig) {
      try {
        const config: VfxViewConfig = JSON.parse(savedConfig)
        const savedColumns = config.visibleMetadataColumns || []
        setSelectedMetadataColumns(savedColumns)
      } catch (error) {
        console.error('Failed to load view config:', error)
        setSelectedMetadataColumns(['Shot Name', 'Description', 'Status', 'Frame Range', 'Duration'])
      }
    } else {
      // Default columns
      setSelectedMetadataColumns(['Shot Name', 'Description', 'Status', 'Frame Range', 'Duration'])
    }
  }, [project.id, availableMetadataFields])

  // Helper functions for bulk tag operations
  const getCommonTags = (shots: VfxShot[]) => {
    if (shots.length === 0) return []
    const tagSets = shots.map(s => new Set(s.tags || []))
    return Array.from(tagSets[0]).filter(tag =>
      tagSets.every(set => set.has(tag))
    )
  }

  const getPartialTags = (shots: VfxShot[]) => {
    const allTags = new Set(shots.flatMap(s => s.tags || []))
    const commonTags = new Set(getCommonTags(shots))
    return Array.from(allTags).filter(tag => !commonTags.has(tag))
  }

  const handleDeleteShot = async (shotId: string, _shotName: string) => {
    if (window.confirm('Are you sure you want to delete this VFX shot?')) {
      try {
        await deleteVfxShot.mutateAsync({ shotId, projectId: project.id })
      } catch (error: any) {
        // Error toast is already handled by the hook
      }
    }
  }

  const handleLinkShot = (shot: VfxShot) => {
    setSelectedShot(shot)
    setLinkDialogOpen(true)
  }

  const handleViewShotDetails = (shot: VfxShot) => {
    setDetailShotId(shot.id)
    setDetailSheetOpen(true)
  }

  // Single shot tag editing
  const handleEditTags = (shot: VfxShot) => {
    setSelectedShot(shot)
    setTagsDialogMode('single')
  }

  const handleSingleSaveTags = async (newTags: string[]) => {
    if (!selectedShot) return

    try {
      await updateVfxShot.mutateAsync({
        shotId: selectedShot.id,
        updates: { tags: newTags }
      })
      setTagsDialogMode(null)
      setSelectedShot(null)
    } catch (error: any) {
      console.error('Failed to update tags:', error)
    }
  }

  // Bulk operations handlers
  const handleBulkEditTags = () => {
    setTagsDialogMode('bulk')
  }

  const handleBulkSaveTags = async (newTags: string[]) => {
    const currentCommonTags = getCommonTags(selection.selectedItems)
    const addTags = newTags.filter(t => !currentCommonTags.includes(t))
    const removeTags = currentCommonTags.filter(t => !newTags.includes(t))

    await bulkUpdateTags.mutateAsync({
      projectId: project.id,
      shotIds: Array.from(selection.selectedIds),
      addTags: addTags.length > 0 ? addTags : undefined,
      removeTags: removeTags.length > 0 ? removeTags : undefined
    })

    setTagsDialogMode(null)
    selection.clearSelection()
  }

  const handleBulkDelete = async () => {
    const count = selection.selectedCount
    if (!window.confirm(`Are you sure you want to delete ${count} VFX shot${count > 1 ? 's' : ''}?`)) {
      return
    }

    try {
      await Promise.all(
        Array.from(selection.selectedIds).map(shotId =>
          deleteVfxShot.mutateAsync({ shotId, projectId: project.id })
        )
      )
      selection.clearSelection()
    } catch (error: any) {
      console.error('Failed to delete shots:', error)
    }
  }

  // Edit mode handlers (using shared hook)
  const handleStartEdit = (shot: VfxShot) => {
    editState.startEdit(shot)
    setExpandedShotId(shot.id)
  }

  const handleCancelEdit = () => {
    editState.cancelEdit()
    setExpandedShotId(null)
  }

  const handleSaveEdit = async () => {
    if (!editState.editingId || !editState.editedData) return

    try {
      await updateVfxShot.mutateAsync({
        shotId: editState.editingId,
        updates: {
          shot_name: editState.editedData.shot_name,
          status: editState.editedData.status,
          frame_range_start: editState.editedData.frame_range_start,
          frame_range_end: editState.editedData.frame_range_end,
          frame_duration: editState.editedData.frame_duration,
          priority: editState.editedData.priority,
          due_date: editState.editedData.due_date,
          notes: editState.editedData.notes,
          assigned_user_id: editState.editedData.assigned_user_id,
          tags: editState.editedData.tags,
          custom_metadata: editState.editedData.custom_metadata,
        }
      })
      editState.clearEdit()
      setExpandedShotId(null)
    } catch (error: any) {
      console.error('Failed to update VFX shot:', error)
    }
  }

  const handleFieldChange = (field: keyof VfxShot, value: any) => {
    editState.updateField(field, value)
  }

  const handleDescriptionChange = (value: string) => {
    if (!editState.editedData) return
    const updatedMetadata = {
      ...editState.editedData.custom_metadata,
      description: value
    }
    editState.updateField('custom_metadata', updatedMetadata)
  }

  const handleToggleTag = (tagName: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagName)
        ? prev.tags.filter(t => t !== tagName)
        : [...prev.tags, tagName]
    }))
  }

  const handleCreateShot = async () => {
    // Get frame range from selected clip or use defaults
    let frameStart = 0
    let frameEnd = 100
    const selectedClip = clips?.find(c => c.id === formData.clip_id)
    if (selectedClip?.duration_frames) {
      frameStart = 0
      frameEnd = selectedClip.duration_frames
    }

    try {
      await createVfxShot.mutateAsync({
        projectId: project.id,
        shotData: {
          project_id: project.id,
          shot_name: formData.shot_name,
          clip_id: formData.clip_id || undefined,
          frame_range_start: frameStart,
          frame_range_end: frameEnd,
          frame_duration: frameEnd - frameStart,
          status: 'yellow',
          tags: formData.tags,
          custom_metadata: {
            description: formData.description || ''
          }
        }
      })

      // Track VFX shot creation (no shot_name for privacy - contains scene/shot details)
      trackVfxShotCreated({
        project_id: project.id,
        status: 'yellow',
        ...(formData.clip_id && { clip_id: formData.clip_id }),
      })

      setCreateDialogOpen(false)
      setFormData({
        shot_name: '',
        description: '',
        clip_id: '',
        tags: [],
      })
      setClipSearchQuery('')
    } catch (error: any) {
      // Error toast is already handled by the hook
    }
  }

  // Helper to get field value for any column
  const getFieldValue = (shot: VfxShot, field: string): string => {
    switch (field) {
      case 'Shot Name':
        return shot.shot_name || '-'
      case 'Description':
        return shot.custom_metadata?.description || '-'
      case 'Status':
        return getStatusLabel(shot.status)
      case 'Frame Range':
        return `${shot.frame_range_start}-${shot.frame_range_end}`
      case 'Duration':
        return `${shot.frame_duration} frames`
      case 'Priority':
        return shot.priority ? String(shot.priority) : '-'
      case 'Due Date':
        return shot.due_date ? formatDueDate(shot.due_date) : '-'
      case 'Assigned User':
        return shot.assigned_user_id ? shot.assigned_user_id.slice(0, 8) + '...' : '-'
      default:
        return '-'
    }
  }

  const getStatusIcon = (status: VfxShotStatus) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'in_progress':
        return <Clock className="h-4 w-4 text-blue-500" />
      case 'review_ready':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />
      case 'green':
        return <CheckCircle className="h-4 w-4 text-emerald-500" />
      case 'yellow':
        return <AlertCircle className="h-4 w-4 text-amber-500" />
      case 'omit':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'back_in':
        return <ArrowRight className="h-4 w-4 text-cyan-500" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusLabel = (status: VfxShotStatus): string => {
    switch (status) {
      case 'yellow':
        return 'Pending'
      case 'green':
        return 'Approved'
      case 'in_progress':
        return 'In Progress'
      case 'review_ready':
        return 'Review'
      case 'approved':
        return 'Approved'
      case 'omit':
        return 'Omitted'
      case 'back_in':
        return 'Back In'
      default:
        return status
    }
  }

  const formatDueDate = (date?: string) => {
    if (!date) return 'No due date'

    const dueDate = new Date(date)
    const now = new Date()
    const diffMs = dueDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays < 0) {
      return `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} overdue`
    } else if (diffDays === 0) {
      return 'Due today'
    } else if (diffDays === 1) {
      return 'Due tomorrow'
    } else {
      return `Due in ${diffDays} days`
    }
  }

  const statusCounts = {
    all: vfxShots.length,
    yellow: vfxShots.filter(s => s.status === 'yellow').length,
    green: vfxShots.filter(s => s.status === 'green').length,
    in_progress: vfxShots.filter(s => s.status === 'in_progress').length,
    review_ready: vfxShots.filter(s => s.status === 'review_ready').length,
    approved: vfxShots.filter(s => s.status === 'approved').length,
    omit: vfxShots.filter(s => s.status === 'omit').length,
    back_in: vfxShots.filter(s => s.status === 'back_in').length,
  }

  return (
    <div className="space-y-6">
      {/* Bulk Actions Bar */}
      <BulkActionBar
        selectedCount={selection.selectedCount}
        onClearSelection={selection.clearSelection}
        onAddTags={handleBulkEditTags}
        onDelete={handleBulkDelete}
        entityType="vfx_shot"
      />

      {/* Actions Bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-md">
          {vfxShots && vfxShots.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search VFX shots... Try: shot:sc001, status:pending"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          )}
        </div>
        <div className="flex gap-2 items-center">
          {/* Sort By Dropdown */}
          {vfxShots && vfxShots.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">Sort by:</label>
              <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="tag">Tag</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {availableMetadataFields.length > 0 && (
            <ColumnPicker
              projectId={project.id}
              availableFields={availableMetadataFields}
              selectedFields={selectedMetadataColumns}
              onSelectionChange={setSelectedMetadataColumns}
            />
          )}
          {permissions.canCreateVfxShots && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Wand2 className="mr-2 h-4 w-4" />
              New VFX Shot
            </Button>
          )}
        </div>
      </div>

      {/* Status Filter Pills */}
      {vfxShots && vfxShots.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('all')}
          >
            All ({statusCounts.all})
          </Button>
          <Button
            variant={filterStatus === 'yellow' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('yellow')}
          >
            Pending ({statusCounts.yellow})
          </Button>
          <Button
            variant={filterStatus === 'green' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('green')}
          >
            Approved ({statusCounts.green})
          </Button>
          <Button
            variant={filterStatus === 'in_progress' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('in_progress')}
          >
            In Progress ({statusCounts.in_progress})
          </Button>
          <Button
            variant={filterStatus === 'review_ready' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('review_ready')}
          >
            Review ({statusCounts.review_ready})
          </Button>
        </div>
      )}

      {/* Search Results Info */}
      {vfxShots && vfxShots.length > 0 && filteredShots.length !== vfxShots.length && (
        <p className="text-sm text-muted-foreground">
          Showing {flattenedShots.length} of {vfxShots.length} VFX shots
        </p>
      )}

      {/* Loading State */}
      {vfxLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading VFX shots...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive text-center">
              Failed to load VFX shots. Please try again.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!vfxLoading && !error && vfxShots?.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Wand2 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No VFX shots yet</h3>
              <p className="text-muted-foreground mb-6">
                {permissions.canCreateVfxShots
                  ? 'Get started by creating your first VFX shot'
                  : 'No VFX shots have been created yet'}
              </p>
              {permissions.canCreateVfxShots && (
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Wand2 className="mr-2 h-4 w-4" />
                  Create Your First VFX Shot
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* VFX Shots Table */}
      {!vfxLoading && !error && flattenedShots.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div
              ref={parentRef}
              className="overflow-auto"
              style={{ height: '600px' }}
            >
              <table className="w-full">
                <thead className="sticky top-0 z-10 bg-black dark:bg-black">
                  <tr className="border-b">
                    <th className="px-4 py-3" style={{ width: '50px' }}>
                      <Checkbox
                        checked={selection.isAllSelected}
                        indeterminate={selection.isSomeSelected}
                        onCheckedChange={selection.toggleAll}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground" style={{ width: '50px' }}>
                    </th>
                    {selectedMetadataColumns.map((field) => (
                      <th key={field} className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        {getMetadataDisplayName(field)}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground" style={{ minWidth: '150px' }}>
                      Tags
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground" style={{ minWidth: '200px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rowVirtualizer.getVirtualItems().length > 0 && (
                    <tr style={{ height: `${rowVirtualizer.getVirtualItems()[0]?.start || 0}px` }} />
                  )}
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const shot = flattenedShots[virtualRow.index]
                    const index = virtualRow.index
                    const isExpanded = expandedShotId === shot.id
                    const isEditing = editState.isEditing(shot.id)
                    const displayShot = isEditing ? editState.editedData : shot

                    return (
                      <React.Fragment key={virtualRow.key}>
                        <SelectableTableRow
                          isSelected={selection.selectedIds.has(shot.id)}
                          isExpanded={isExpanded}
                          isEditing={isEditing}
                          onClick={() => !isEditing && handleViewShotDetails(shot)}
                          index={virtualRow.index}
                        >
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selection.selectedIds.has(shot.id)}
                              onCheckedChange={(e) => selection.toggleSelection(shot.id, e)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <ExpandableRowButton
                              isExpanded={isExpanded}
                              onToggle={(e) => {
                                e.stopPropagation()
                                setExpandedShotId(isExpanded ? null : shot.id)
                              }}
                              disabled={isEditing}
                            />
                          </td>
                          {selectedMetadataColumns.map((field) => (
                            <td key={field} className="px-4 py-3 text-sm text-muted-foreground">
                              {!isEditing && (
                                <div className="flex items-center gap-2">
                                  {field === 'Status' && getStatusIcon(shot.status)}
                                  {getFieldValue(shot, field)}
                                </div>
                              )}
                              {isEditing && field === 'Shot Name' && (
                                <Input
                                  value={displayShot.shot_name || ''}
                                  onChange={(e) => handleFieldChange('shot_name', e.target.value)}
                                  className="h-8 text-sm"
                                  placeholder="Shot name"
                                />
                              )}
                              {isEditing && field === 'Description' && (
                                <Input
                                  value={displayShot.custom_metadata?.description || ''}
                                  onChange={(e) => handleDescriptionChange(e.target.value)}
                                  className="h-8 text-sm"
                                  placeholder="Description"
                                />
                              )}
                              {isEditing && field === 'Status' && (
                                <Select
                                  value={displayShot.status || 'yellow'}
                                  onValueChange={(value) => handleFieldChange('status', value as VfxShotStatus)}
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="yellow">Pending</SelectItem>
                                    <SelectItem value="green">Approved</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="review_ready">Review</SelectItem>
                                    <SelectItem value="approved">Final Approved</SelectItem>
                                    <SelectItem value="omit">Omit</SelectItem>
                                    <SelectItem value="back_in">Back In</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                              {isEditing && field === 'Frame Range' && (
                                <div className="flex gap-1">
                                  <Input
                                    type="number"
                                    value={displayShot.frame_range_start || ''}
                                    onChange={(e) => handleFieldChange('frame_range_start', parseInt(e.target.value) || 0)}
                                    className="h-8 text-sm w-20"
                                    placeholder="Start"
                                  />
                                  <span className="self-center">-</span>
                                  <Input
                                    type="number"
                                    value={displayShot.frame_range_end || ''}
                                    onChange={(e) => handleFieldChange('frame_range_end', parseInt(e.target.value) || 0)}
                                    className="h-8 text-sm w-20"
                                    placeholder="End"
                                  />
                                </div>
                              )}
                              {isEditing && field === 'Duration' && (
                                <Input
                                  type="number"
                                  value={displayShot.frame_duration || ''}
                                  onChange={(e) => handleFieldChange('frame_duration', parseInt(e.target.value) || 0)}
                                  className="h-8 text-sm"
                                  placeholder="Duration"
                                />
                              )}
                              {isEditing && field === 'Priority' && (
                                <Input
                                  type="number"
                                  value={displayShot.priority || ''}
                                  onChange={(e) => handleFieldChange('priority', parseInt(e.target.value) || 0)}
                                  className="h-8 text-sm"
                                  placeholder="Priority"
                                />
                              )}
                              {isEditing && field === 'Due Date' && (
                                <Input
                                  type="date"
                                  value={displayShot.due_date || ''}
                                  onChange={(e) => handleFieldChange('due_date', e.target.value)}
                                  className="h-8 text-sm"
                                />
                              )}
                              {isEditing && field === 'Assigned User' && (
                                <Input
                                  value={displayShot.assigned_user_id || ''}
                                  onChange={(e) => handleFieldChange('assigned_user_id', e.target.value)}
                                  className="h-8 text-sm"
                                  placeholder="User ID"
                                />
                              )}
                              {isEditing && !['Shot Name', 'Description', 'Status', 'Frame Range', 'Duration', 'Priority', 'Due Date', 'Assigned User'].includes(field) && (
                                getFieldValue(shot, field)
                              )}
                            </td>
                          ))}
                          <td className="px-4 py-3">
                            <TagsDisplayCell tags={shot.tags || []} maxVisible={2} />
                          </td>
                          <td className="px-4 py-3">
                            <EntityRowActions
                              isEditing={isEditing}
                              onEdit={() => handleStartEdit(shot)}
                              onTags={() => handleEditTags(shot)}
                              onLink={() => handleLinkShot(shot)}
                              onDelete={() => handleDeleteShot(shot.id, shot.shot_name)}
                              onSave={handleSaveEdit}
                              onCancel={handleCancelEdit}
                              canEdit={permissions.canEditVfxShots}
                              canLink={permissions.canLinkVfxShots}
                              isSaving={updateVfxShot.isPending}
                            />
                          </td>
                        </SelectableTableRow>
                        {/* Expanded detail row - View mode */}
                        <ExpandedMetadataRow
                          colSpan={4 + selectedMetadataColumns.length}
                          isExpanded={isExpanded && !isEditing}
                          metadata={{
                            source_timecode: null,
                            duration_frames: null,
                            fps: null,
                            source_file_path: null,
                            custom_metadata: shot.custom_metadata
                          }}
                          additionalFields={[
                            { label: 'Start Frame', value: shot.frame_range_start },
                            { label: 'End Frame', value: shot.frame_range_end },
                            { label: 'Duration', value: `${shot.frame_duration} frames` },
                            { label: 'Priority', value: shot.priority || 'Normal' },
                            ...(shot.due_date ? [
                              { label: 'Due Date', value: new Date(shot.due_date).toLocaleDateString() },
                              { label: 'Status', value: formatDueDate(shot.due_date) }
                            ] : []),
                            ...(shot.custom_metadata?.description ? [{ label: 'Description', value: shot.custom_metadata.description }] : []),
                            ...(shot.notes ? [{ label: 'Notes', value: shot.notes }] : []),
                          ]}
                        />

                        {/* Expanded detail row - Edit mode */}
                        {isEditing && (
                          <tr
                            key={`${shot.id}-expanded-edit`}
                            className={`
                              border-b
                              ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}
                              bg-blue-50/50 dark:bg-blue-950/20
                            `}
                          >
                            <td colSpan={4 + selectedMetadataColumns.length} className="px-4 py-3">
                              <div className="ml-10 p-4 bg-muted/30 rounded-md border border-border">
                                <h4 className="text-sm font-semibold mb-3 text-muted-foreground">
                                  Edit VFX Shot Details
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                  <div className="space-y-1">
                                    <div className="text-xs font-medium text-muted-foreground uppercase">
                                      Notes
                                    </div>
                                    <Input
                                      value={displayShot.notes || ''}
                                      onChange={(e) => handleFieldChange('notes', e.target.value)}
                                      className="h-8 text-sm"
                                      placeholder="Add notes..."
                                    />
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                  {rowVirtualizer.getVirtualItems().length > 0 && (
                    <tr style={{
                      height: `${
                        rowVirtualizer.getTotalSize() -
                        (rowVirtualizer.getVirtualItems()[rowVirtualizer.getVirtualItems().length - 1]?.start || 0) -
                        (rowVirtualizer.getVirtualItems()[rowVirtualizer.getVirtualItems().length - 1]?.size || 0)
                      }px`
                    }} />
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Search Results */}
      {!vfxLoading && !error && vfxShots && vfxShots.length > 0 && flattenedShots.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Search className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No VFX shots found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search or filter
              </p>
              <Button variant="outline" onClick={() => {
                setSearchQuery('')
                setFilterStatus('all')
              }}>
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create VFX Shot Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create VFX Shot</DialogTitle>
            <DialogDescription>
              Add a new VFX shot to this project. Frame ranges will be set from clip duration if linked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="shot_name">Shot Name</Label>
              <Input
                id="shot_name"
                placeholder="e.g., Shot_010_BG1_V001"
                value={formData.shot_name}
                onChange={(e) => setFormData({ ...formData, shot_name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Describe what needs to be done for this VFX shot"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="clip_link">Link to Media Clip (Optional)</Label>
              <Input
                id="clip_search"
                placeholder="Search clips by name..."
                value={clipSearchQuery}
                onChange={(e) => setClipSearchQuery(e.target.value)}
                className="mb-2"
              />
              {formData.clip_id ? (
                <div className="flex items-center justify-between p-3 border rounded-md bg-muted/50">
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {clips?.find(c => c.id === formData.clip_id)?.name || 'Unknown clip'}
                    </p>
                    {clips?.find(c => c.id === formData.clip_id)?.duration_frames && (
                      <p className="text-xs text-muted-foreground">
                        {clips.find(c => c.id === formData.clip_id)?.duration_frames} frames
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData({ ...formData, clip_id: '' })}
                  >
                    Clear
                  </Button>
                </div>
              ) : (
                <div className="border rounded-md p-3 max-h-[200px] overflow-y-auto">
                  {clips && clips.length > 0 ? (
                    <div className="space-y-1">
                      {clips
                        .filter(clip =>
                          !clipSearchQuery ||
                          clip.name.toLowerCase().includes(clipSearchQuery.toLowerCase())
                        )
                        .slice(0, 20)
                        .map((clip) => (
                          <button
                            key={clip.id}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, clip_id: clip.id })
                              setClipSearchQuery('')
                            }}
                            className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors"
                          >
                            <p className="text-sm font-medium">{clip.name}</p>
                            {clip.duration_frames && (
                              <p className="text-xs text-muted-foreground">
                                {clip.duration_frames} frames
                              </p>
                            )}
                          </button>
                        ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No clips available in this project
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tags (Optional)</Label>
              <div className="flex flex-wrap gap-2 min-h-[40px] p-3 border rounded-md">
                {formData.tags.length > 0 ? (
                  formData.tags.map((tagName) => {
                    const tag = tags?.find((t) => t.name === tagName)
                    return (
                      <TagBadge
                        key={tagName}
                        tag={tag || tagName}
                        color={tag?.color}
                        onRemove={() => handleToggleTag(tagName)}
                      />
                    )
                  })
                ) : (
                  <span className="text-sm text-muted-foreground">No tags selected</span>
                )}
              </div>

              {tags && tags.length > 0 && (
                <div className="border rounded-md p-3 max-h-[200px] overflow-y-auto">
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => handleToggleTag(tag.name)}
                        className={`transition-opacity ${
                          formData.tags.includes(tag.name) ? 'opacity-40' : ''
                        }`}
                      >
                        <TagBadge tag={tag} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateShot}
              disabled={!formData.shot_name || createVfxShot.isPending}
            >
              {createVfxShot.isPending ? 'Creating...' : 'Create Shot'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VFX Shot Detail Sidebar */}
      {detailShotId && (
        <EntityDetailSheet
          open={detailSheetOpen}
          onOpenChange={setDetailSheetOpen}
          title="VFX Shot Details"
          width="xl"
        >
          <VfxShotDetailContent
            shotId={detailShotId}
            projectId={project.id}
          />
        </EntityDetailSheet>
      )}

      {/* Edit Tags Dialog - Shared component */}
      <TagsDialog
        open={tagsDialogMode !== null}
        onOpenChange={(open) => !open && setTagsDialogMode(null)}
        currentTags={tagsDialogMode === 'single' ? (selectedShot?.tags || []) : []}
        commonTags={tagsDialogMode === 'bulk' ? getCommonTags(selection.selectedItems) : undefined}
        partialTags={tagsDialogMode === 'bulk' ? getPartialTags(selection.selectedItems) : undefined}
        availableTags={tags}
        onSave={tagsDialogMode === 'bulk' ? handleBulkSaveTags : handleSingleSaveTags}
        isBulkMode={tagsDialogMode === 'bulk'}
        entityName={tagsDialogMode === 'single' ? selectedShot?.shot_name : undefined}
        selectedCount={selection.selectedCount}
      />

      {/* Link Relationship Dialog */}
      {selectedShot && (
        <LinkRelationshipDialog
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
          sourceType="vfx_shot"
          sourceId={selectedShot.id}
          sourceName={selectedShot.shot_name}
          clips={clips || []}
          vfxShots={vfxShots}
          allTags={tags.map(t => t.name)}
          onLinkClip={async (clipId, relationshipType) => {
            if (!selectedShot) return
            try {
              await linkClipToVfxShot(selectedShot.id, clipId, relationshipType as any || 'source')
              toast.success('Clip linked to VFX shot successfully')
            } catch (error: any) {
              console.error('Failed to link clip to VFX shot:', error)
              toast.error(error.message || 'Failed to link clip to VFX shot')
              throw error
            }
          }}
          onLinkVfxShot={async (_vfxShotId) => {
            toast.info('VFX shot linking not yet implemented')
          }}
        />
      )}
    </div>
  )
}
